import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Plus, Minus, X, Check, ArrowRight, AlertTriangle } from "lucide-react";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";
import JsonLd from "@/components/JsonLd";
import SeoFooter from "@/components/SeoFooter";
import { findProblem, problemPaths, ORIGIN } from "@/data/seoPages";

const GOLD = "#C7A668";

export default function ProblemPage({ lang: routeLang }) {
  const ctx = useLang();
  const lang = routeLang || ctx.lang;
  const L = (en, es) => (lang === "en" ? en : es);
  const { slug } = useParams();
  const e = findProblem(slug, lang);
  const [open, setOpen] = useState(0);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!e) return <Navigate to={lang === "en" ? "/en" : "/"} replace />;

  const c = e[lang];
  const esPath = problemPaths(e, "es");
  const enPath = problemPaths(e, "en");
  const canonical = ORIGIN + (lang === "en" ? enPath : esPath);
  const priorityPath = lang === "en" ? "/en/priority" : "/priority";
  const ogImage = `${ORIGIN}/og/em-${e.key}-${lang}.png`;

  const jsonld = { "@context": "https://schema.org", "@graph": [
    { "@type": "FAQPage", url: canonical, mainEntity: c.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    { "@type": "WebPage", name: c.title, description: c.desc, url: canonical, inLanguage: lang === "en" ? "en" : "es-MX", about: { "@type": "ProfessionalService", name: "dukk", url: ORIGIN }, provider: { "@type": "ProfessionalService", name: "dukk", url: ORIGIN } },
    { "@type": "BreadcrumbList", itemListElement: [ { "@type": "ListItem", position: 1, name: L("Home", "Inicio"), item: ORIGIN + (lang === "en" ? "/en" : "/") }, { "@type": "ListItem", position: 2, name: c.h1, item: canonical } ] },
  ] };

  const PriorityCta = ({ id }) => (
    <Link to={priorityPath} data-testid={id} className="tb-cta" style={{ background: GOLD, color: "#0a0a0a", fontWeight: 600, borderRadius: 4, padding: "13px 24px", fontSize: 14, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>{L("Request Priority Response", "Solicitar respuesta prioritaria")}<ArrowRight size={15} /></Link>
  );

  return (
    <div className="grain" style={{ minHeight: "100vh", paddingTop: 110 }}>
      <PageSeo esPath={esPath} enPath={enPath} title={c.title} description={c.desc} lang={lang} image={ogImage} />
      <JsonLd data={jsonld} id="jsonld-problem" />
      <article data-testid="problem-page" style={{ maxWidth: 880, margin: "0 auto", padding: "0 clamp(20px,5vw,40px)" }}>
        <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".18em", color: GOLD, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={13} />{c.kicker.toUpperCase()}</div>
        <h1 style={{ fontSize: "clamp(28px,4.4vw,48px)", fontWeight: 400, margin: 0, letterSpacing: "-.02em", lineHeight: 1.08 }}>{c.h1}</h1>
        <p style={{ fontSize: "clamp(16px,2vw,19px)", color: "#C7CBD0", lineHeight: 1.65, margin: "22px 0 0", maxWidth: 760 }}>{c.reassure}</p>

        <div style={{ marginTop: 28 }}><PriorityCta id="problem-cta-top" /></div>

        <section style={{ marginTop: 44, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
          <div style={{ border: "1px solid rgba(248,113,113,.25)", background: "rgba(248,113,113,.04)", borderRadius: 8, padding: "22px 24px" }}>
            <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 14px", color: "#f1a1a1" }}>{L("What not to do", "Qué NO hacer")}</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 11 }}>
              {c.dont.map((d, i) => (
                <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14.5, color: "#cfd3d8", lineHeight: 1.5 }}><X size={15} color="#f1a1a1" style={{ marginTop: 2, flexShrink: 0 }} />{d}</li>
              ))}
            </ul>
          </div>
          <div style={{ border: "1px solid rgba(199,166,104,.3)", background: "rgba(199,166,104,.05)", borderRadius: 8, padding: "22px 24px" }}>
            <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 14px", color: GOLD }}>{L("What dukk can do", "Qué hace dukk")}</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 11 }}>
              {c.can.map((d, i) => (
                <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14.5, color: "#d7dadd", lineHeight: 1.5 }}><Check size={15} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />{d}</li>
              ))}
            </ul>
          </div>
        </section>

        <div style={{ marginTop: 36, padding: "24px clamp(20px,3vw,30px)", border: "1px solid rgba(199,166,104,.3)", background: "rgba(199,166,104,.05)", borderRadius: 8 }}>
          <p style={{ fontSize: 16, color: "#E7E4DD", margin: "0 0 16px", lineHeight: 1.55 }}>{L("Private aircraft interiors include delicate materials. A professional should validate the surface, material and product before any treatment.", "Los interiores de aeronaves privadas incluyen materiales delicados. Un profesional debe validar la superficie, el material y el producto antes de cualquier tratamiento.")}</p>
          <PriorityCta id="problem-cta-mid" />
        </div>

        <h2 style={{ fontSize: "clamp(22px,3vw,30px)", fontWeight: 400, margin: "52px 0 8px", letterSpacing: "-.01em" }}>{L("Frequently asked", "Preguntas frecuentes")}</h2>
        <div style={{ marginTop: 18, borderTop: "1px solid rgba(255,255,255,.1)" }}>
          {c.faq.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`problem-faq-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,.1)" }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`problem-faq-toggle-${i}`} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "20px 0", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <h3 style={{ fontSize: "clamp(15px,2vw,19px)", fontWeight: 400, margin: 0, color: "#E7E4DD" }}>{f.q}</h3>
                  {isOpen ? <Minus size={18} color={GOLD} style={{ flexShrink: 0 }} /> : <Plus size={18} color="#7a808a" style={{ flexShrink: 0 }} />}
                </button>
                {isOpen && <p style={{ fontSize: 15, color: "#B8BDC3", lineHeight: 1.7, margin: "0 0 22px", maxWidth: 720 }}>{f.a}</p>}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 40 }}><PriorityCta id="problem-cta-bottom" /></div>
      </article>
      <SeoFooter />
    </div>
  );
}
